import { SSE } from 'sse.js';
import { JsonObject } from '../helpers';
import Environment from '../environment';
import { AuthProvider, safeRefreshTokenUser } from './auth.service';
import { getTimestamp } from '../date-helpers';
import { Citation, ThreadContext, Violations } from '../models/thread';
import { ThreadMessageModel } from '../models/thread/thread-message.model';
import { ThreadModel } from '../models/thread/thread.model';
import { addError } from '../datadog';
import { ORG_DEFAULT_AGENT_ID } from './shortcut.service';

export type StreamPayload = {
  conversationId: string;
  isComplete: boolean;
  messages: { role: string; content: string; citations?: Citation[] }[];
  error?: string;
  responseType: 'history' | 'messages';
  violations?: Violations;
};

export type StreamMessage = {
  threadId: string;
  isComplete: boolean;
  content: string;
  error?: string;
  errorCode?: number;
  threadMessage?: ThreadMessageModel;
  locked?: boolean;
};

export type StreamControlSignal = 'data-source-review' | 'test';

let sseClient: SSE | undefined = undefined;

const startStreamClient = (path: string, data: JsonObject): SSE => {
  sseClient = new SSE(`${Environment.SP_EDGE_API_URL}${path}`, {
    headers: {
      'Content-Type': 'application/json',
      'Surepath-Authorization': `Bearer ${AuthProvider.token}`,
    },
    payload: JSON.stringify(data),
    method: 'POST',
    start: true,
  });

  return sseClient;
};

export const stopStreamClient = (): boolean => {
  if (!sseClient) {
    return false;
  }

  if (sseClient.readyState === EventSource.CLOSED) {
    return false;
  }

  sseClient.close();
  return true;
};

export const streamUserMessage = (
  message: ThreadMessageModel,
  onMessage: (data: StreamMessage) => void,
  onContext?: (threadContext: ThreadContext) => void,
  onControl?: (controlSignal: StreamControlSignal | null) => void
): boolean => {
  const { threadId, content, shortcut, datasourceIds, privateModelId, agentId } = message;
  const isDefaultAgent = agentId === ORG_DEFAULT_AGENT_ID;

  const postData = {
    messages: [{ role: 'user', content, shortcut, datasourceIds }],
    conversationId: threadId,
    assistantId: isDefaultAgent ? '' : agentId,
    privateModelId: isDefaultAgent ? privateModelId : '',
  };

  const streamClient = startStreamClient('/message/stream', postData);

  let controlSignal: StreamControlSignal | null = null;
  const handleOnControl = (updatedSignal: StreamControlSignal | null, force?: boolean) => {
    if (!force && updatedSignal === controlSignal) {
      return;
    }
    controlSignal = updatedSignal;
    onControl?.(updatedSignal);
  };

  // reset on start
  handleOnControl(null, true);

  // message events
  streamClient.addEventListener('message', (event: EventTarget & { data: string }) => {
    try {
      const data = JSON.parse(event.data) as StreamPayload;
      const { isComplete, messages, conversationId, responseType, violations } = data;

      if (responseType === 'history') {
        const historyThread = new ThreadModel({ conversation: messages });
        onContext?.(historyThread.context);
        return;
      }

      if (responseType !== 'messages') {
        console.error('unknown sse response type', data);
        return;
      }

      // response validation, @todo handle multiple messages
      if (!messages?.length || messages[0].role !== 'assistant') {
        console.error('invalid sse response', data);
        return;
      }

      const content = messages[0].content;
      let threadMessage: ThreadMessageModel | undefined;

      // @todo switch to a more discrete detection method once gateway supports it
      if (String(content).toLowerCase().startsWith('*reviewing data sources')) {
        handleOnControl('data-source-review');
        return;
      }

      if (controlSignal) {
        handleOnControl(null);
      }

      if (isComplete) {
        threadMessage = new ThreadMessageModel({
          threadId,
          role: messages[0].role,
          content,
          timestamp: getTimestamp(new Date(), true) || 0,
          violations: violations || {
            sensitiveData: false,
            intent: false,
            access: false,
            input: false,
            output: false,
          },
          citations: messages[0].citations || [],
          agentId,
          privateModelId,
        });
      }

      onMessage({
        threadId: conversationId,
        isComplete,
        content,
        error: '',
        threadMessage,
      });
    } catch (err) {
      console.error(err);
    }
  });

  // error events
  streamClient.addEventListener(
    'error',
    (event: EventTarget & { responseCode: number; data: string }) => {
      addError({ ...event, name: 'stream-error', message: event.data });
      const { responseCode } = event;

      if (responseCode === 401) {
        safeRefreshTokenUser();
      }

      onMessage({
        threadId,
        isComplete: true,
        content: '',
        error: 'stream error',
        errorCode: responseCode,
      });
    }
  );

  return true;
};
